import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

/**
 * Statsig feature gate that AUTHORIZES the `?is_ds=true` preview override — in any environment,
 * including production. It is NOT the DS decision itself: the two-level rollout
 * (`ds_rollout_page_config__web` + `RAMP_ds-rollout__web`) owns that (see `resolveDSRolloutGateValue`).
 * Fail-closed: with the gate off (or Statsig unreachable) the query param is ignored everywhere.
 */
export const DESIGN_SYSTEM_GATE_NAME = 'ssr-use-design-system-components'
export const DESIGN_SYSTEM_QUERY_PARAM_KEY = 'is_ds'

export const DOCUMENT_HTML_DATA_STYLES_ATTR = 'data-styles'
export const DOCUMENT_HTML_DATA_STYLES_VALUE = 'panda'

// Marks `<html>` for DS ramp `test`-bucket users (independent of the page's own DS decision).
export const DOCUMENT_HTML_DATA_DS_RAMP_USER_ATTR = 'data-ds-ramp-user'
export const DOCUMENT_HTML_DATA_DS_RAMP_USER_VALUE = 'true'

/**
 * Per-request DS decision for server renders. Written by `_app` from
 * `pageProps.isDesignSystemEnabled` (computed in `getServerSideBaseProps` by the two-level
 * DS rollout — `ds_rollout_page_config__web` + `RAMP_ds-rollout__web` — or the gate-authorized
 * `?is_ds=true` override). Safe as module state because the pages-router render pass is
 * synchronous. Browser reads never touch this — they read the `<html>` attribute.
 */
let designSystemEnabledForRender = false

export function setDesignSystemEnabledInMemory(enabled: boolean | undefined | null): void {
  designSystemEnabledForRender = Boolean(enabled)

  if (isBrowser()) {
    if (designSystemEnabledForRender) {
      document.documentElement.setAttribute(DOCUMENT_HTML_DATA_STYLES_ATTR, DOCUMENT_HTML_DATA_STYLES_VALUE)
    } else {
      document.documentElement.removeAttribute(DOCUMENT_HTML_DATA_STYLES_ATTR)
    }
  }
}

export function shouldUseDesignSystem(): boolean {
  if (isBrowser()) {
    return document.documentElement.getAttribute(DOCUMENT_HTML_DATA_STYLES_ATTR) === DOCUMENT_HTML_DATA_STYLES_VALUE
  }

  return designSystemEnabledForRender
}

/** Ramp `test` membership for this request (independent of page DS). SSR module flag + `<html>` attr. */
let dsRampUserForRender = false

export function setDSRampUserInMemory(enabled: boolean | undefined | null): void {
  dsRampUserForRender = Boolean(enabled)

  // Mirror onto `<html>` on client navigations (`_document` only stamps full loads).
  if (isBrowser()) {
    if (dsRampUserForRender) {
      document.documentElement.setAttribute(DOCUMENT_HTML_DATA_DS_RAMP_USER_ATTR, DOCUMENT_HTML_DATA_DS_RAMP_USER_VALUE)
    } else {
      document.documentElement.removeAttribute(DOCUMENT_HTML_DATA_DS_RAMP_USER_ATTR)
    }
  }
}

export function isDSRampUser(): boolean {
  if (isBrowser()) {
    return (
      document.documentElement.getAttribute(DOCUMENT_HTML_DATA_DS_RAMP_USER_ATTR) ===
      DOCUMENT_HTML_DATA_DS_RAMP_USER_VALUE
    )
  }

  return dsRampUserForRender
}

/** DS chrome when the page is DS, or the user is in the DS ramp `test` bucket. */
export function shouldUseDSHeaderFooter(): boolean {
  return shouldUseDesignSystem() || isDSRampUser()
}

/** Hybrid only: align DS chrome to the legacy 7.6rem / 1600px gutter (not DS 48px / 1504). */
export function shouldAlignDSChromeToLegacyPageGutter(): boolean {
  return isDSRampUser() && !shouldUseDesignSystem()
}

export function isDesignSystemEnabledFromNextData(nextData: {
  props?: {
    pageProps?: {
      isDesignSystemEnabled?: boolean
      userAgnosticGlobalFeatureGates?: Record<string, boolean | undefined>
    }
  }
  query?: Record<string, string | string[] | undefined>
}): boolean {
  // The SSR-computed decision is authoritative in EVERY environment, including
  // production. This is the same value `shouldUseDesignSystem()` reads
  // server-side, and this function is the sole writer of `data-styles="panda"`
  // on `<html>` (`_document.page.tsx`). If the check were placed behind the
  // gate-authorized preview fallback below, production SSR would render DS while
  // `_document` left the attribute unset → client reads legacy → hydration
  // mismatch, and DS could never ship to prod traffic via the rollout.
  if (nextData.props?.pageProps?.isDesignSystemEnabled) {
    return true
  }

  // The `is_ds` query-param override is a QA affordance authorized exclusively by the
  // `ssr-use-design-system-components` Statsig gate — honored in ANY environment (including
  // production) while the gate is on, ignored everywhere while it is off. The gate ships in
  // page props (`FEATURE_GATE_REGISTRY`), so this stays in lockstep with the SSR-side check
  // in `getServerSideBaseProps`.
  if (nextData.props?.pageProps?.userAgnosticGlobalFeatureGates?.[DESIGN_SYSTEM_GATE_NAME]) {
    const queryValue = nextData.query?.[DESIGN_SYSTEM_QUERY_PARAM_KEY]
    return (Array.isArray(queryValue) ? queryValue[0] : queryValue) === 'true'
  }

  return false
}

/** SSR ramp prop for `_document`'s initial `data-ds-ramp-user` stamp (QA override is folded server-side). */
export function isDSRampUserFromNextData(nextData: {
  props?: {
    pageProps?: {
      isDSRampUser?: boolean
    }
  }
}): boolean {
  return Boolean(nextData.props?.pageProps?.isDSRampUser)
}
